import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "Mission", href: "/#mission" },
  { label: "Programs", href: "/#programs" },
  { label: "Eligibility", href: "/#eligibility" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Transparency", href: "/#transparency" },
  { label: "Contact", href: "/#contact" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    
    // Check for auth token in localStorage
    const token = localStorage.getItem("hackclub_token");
    const userData = localStorage.getItem("user_data");
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href.startsWith("/#")) {
      const sectionId = href.replace("/#", "");
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleHackClubLogin = () => {
    window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/login`;
  };

  const handleLogout = () => {
    localStorage.removeItem("hackclub_token");
    localStorage.removeItem("user_data");
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full bg-white transition-shadow duration-200 ${
        scrolled ? "shadow-sm border-b border-gray-100" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 shrink-0"
          >
            <div className="w-8 h-8 bg-[#0056D2] rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm font-body">GR</span>
            </div>
            <span className="font-heading text-lg font-bold text-[#111827] hidden sm:block">
              GlitchRealm Foundation
            </span>
            <span className="font-heading text-lg font-bold text-[#111827] sm:hidden">
              GR Foundation
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.label}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleNavClick(link.href)}
                className="px-3 py-2 text-sm font-medium text-[#4B5563] hover:text-[#0056D2] transition-colors rounded-md hover:bg-[#F3F4F6]"
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center gap-2 ml-2">
              {isAuthenticated ? (
                <>
                  <span className="text-sm text-[#4B5563]">
                    {user?.first_name && `Hi, ${user.first_name}`}
                  </span>
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="text-[#0056D2] border-[#0056D2] hover:bg-[#F3F4F6]"
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleHackClubLogin}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-md px-4"
                  >
                    Sign in with Hack Club
                  </Button>
                  <Link to="/apply">
                    <Button className="bg-[#0056D2] hover:bg-[#0044A5] text-white rounded-md px-5">
                      Apply Now
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-md text-[#4B5563] hover:bg-[#F3F4F6]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <nav
            data-testid="mobile-nav"
            className="lg:hidden pb-4 border-t border-gray-100 pt-3"
          >
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleNavClick(link.href)}
                  className="px-3 py-2.5 text-sm font-medium text-[#4B5563] hover:text-[#0056D2] hover:bg-[#F3F4F6] rounded-md text-left"
                >
                  {link.label}
                </button>
              ))}
              <div className="mt-2 flex flex-col gap-2">
                {isAuthenticated ? (
                  <>
                    {user?.first_name && (
                      <div className="px-3 py-2 text-sm text-[#4B5563]">
                        Hi, {user.first_name}
                      </div>
                    )}
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      className="w-full text-[#0056D2] border-[#0056D2]"
                    >
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={handleHackClubLogin}
                      className="w-full bg-red-600 hover:bg-red-700 text-white rounded-md"
                    >
                      Sign in with Hack Club
                    </Button>
                    <Link to="/apply" onClick={() => setMobileOpen(false)}>
                      <Button className="w-full bg-[#0056D2] hover:bg-[#0044A5] text-white rounded-md">
                        Apply Now
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
